import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { motion } from "framer-motion";
import { API_URL } from "../helpers/constants";
import { getToken } from "../helpers/tokenStorage";
import {
  glassCard,
  heroText,
  pageBackground,
  motionVariants,
} from "../helpers/designSystem";

/**
 * SCK-632 — customer-facing LLM spend dashboard.
 *
 * Pulls three passes over /api/me/llm-spend for the same window:
 *   • groupBy=project — powers the per-project breakdown table + chart
 *   • groupBy=route   — powers the per-route breakdown table
 *   • groupBy=day     — powers the projection / MoM math
 *
 * When the coord source is still the SCK-632 stub (source:"stub"),
 * every response is an empty list. We render a friendly empty state
 * per the ticket rather than dashes-everywhere.
 */

const RANGE_OPTIONS = [
  { id: "7d", label: "7d", days: 7 },
  { id: "30d", label: "30d", days: 30 },
  { id: "90d", label: "90d", days: 90 },
  { id: "custom", label: "Custom", days: null },
];

const isoDay = (d) => d.toISOString().slice(0, 10);

const parseIsoOrNull = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d : null;
};

const rangeToInstants = (rangeId, customStart, customEnd) => {
  const now = new Date();
  if (rangeId === "custom") {
    const start = parseIsoOrNull(customStart);
    const end = parseIsoOrNull(customEnd);
    if (!start || !end || start >= end) return { start: null, end: null };
    return { start, end };
  }
  const opt = RANGE_OPTIONS.find((o) => o.id === rangeId) || RANGE_OPTIONS[1];
  const start = new Date(now.getTime() - opt.days * 24 * 60 * 60 * 1000);
  return { start, end: now };
};

const formatDollars = (n) => {
  if (n == null || Number.isNaN(n)) return "$0.00";
  if (Math.abs(n) < 0.01) return `$${n.toFixed(4)}`;
  if (Math.abs(n) < 1) return `$${n.toFixed(3)}`;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
};

const formatTokens = (n) => {
  if (n == null || Number.isNaN(n)) return "0";
  return n.toLocaleString();
};

const formatDelta = (n) => {
  if (n == null || Number.isNaN(n) || !Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

const sumCost = (rows) =>
  (rows || []).reduce((acc, r) => acc + (Number(r.costUsd) || 0), 0);

/**
 * Straight-line projection: (spend so far) / (days elapsed) × (days in
 * month). Kept intentionally simple — the KPI is a projection, not a
 * forecast, and a smarter model can slot in behind the same signature.
 */
const projectMonth = (dailyRows) => {
  if (!dailyRows || dailyRows.length === 0) return 0;
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysElapsed = now.getDate(); // day-of-month
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const spendSoFar = dailyRows
    .filter((r) => {
      const d = new Date(r.groupKey);
      return Number.isFinite(d.getTime()) && d >= monthStart;
    })
    .reduce((acc, r) => acc + (Number(r.costUsd) || 0), 0);
  if (daysElapsed <= 0) return spendSoFar;
  return (spendSoFar / daysElapsed) * daysInMonth;
};

const mtdSpend = (dailyRows) => {
  if (!dailyRows || dailyRows.length === 0) return 0;
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return dailyRows
    .filter((r) => {
      const d = new Date(r.groupKey);
      return Number.isFinite(d.getTime()) && d >= monthStart;
    })
    .reduce((acc, r) => acc + (Number(r.costUsd) || 0), 0);
};

const lastMonthSpend = (dailyRows) => {
  if (!dailyRows || dailyRows.length === 0) return 0;
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return dailyRows
    .filter((r) => {
      const d = new Date(r.groupKey);
      return Number.isFinite(d.getTime()) && d >= lastStart && d < monthStart;
    })
    .reduce((acc, r) => acc + (Number(r.costUsd) || 0), 0);
};

const momDeltaPct = (dailyRows) => {
  const mtd = mtdSpend(dailyRows);
  const last = lastMonthSpend(dailyRows);
  if (last <= 0) return null; // undefined → UI shows a dash
  return ((mtd - last) / last) * 100;
};

const KpiCard = ({ label, value, sub, testId }) => (
  <Card sx={{ minWidth: 160, flex: "1 1 200px" }} data-testid={testId}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
      {sub != null && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const BreakdownTable = ({ title, rows, keyLabel, testId, emptyText }) => (
  <TableContainer component={Paper} sx={{ mt: 2 }} data-testid={testId}>
    <Box sx={{ p: 2, pb: 0 }}>
      <Typography variant="h6">{title}</Typography>
    </Box>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{keyLabel}</TableCell>
          <TableCell align="right">Prompt tokens</TableCell>
          <TableCell align="right">Completion tokens</TableCell>
          <TableCell align="right">Requests</TableCell>
          <TableCell align="right">Cost (USD)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {emptyText}
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          rows.map((r) => (
            <TableRow key={r.groupKey} hover>
              <TableCell>{r.label || r.groupKey}</TableCell>
              <TableCell align="right">{formatTokens(r.promptTokens)}</TableCell>
              <TableCell align="right">{formatTokens(r.completionTokens)}</TableCell>
              <TableCell align="right">{formatTokens(r.requestCount)}</TableCell>
              <TableCell align="right">{formatDollars(r.costUsd)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

async function fetchSpend({ groupBy, start, end }) {
  const params = new URLSearchParams({ groupBy });
  if (start) params.set("rangeStart", start.toISOString());
  if (end) params.set("rangeEnd", end.toISOString());
  const token = getToken();
  const res = await fetch(`${API_URL}/me/llm-spend?${params.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    throw new Error(`llm-spend failed (HTTP ${res.status})`);
  }
  return res.json();
}

const LlmSpendPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [rangeId, setRangeId] = useState("30d");
  const [customStart, setCustomStart] = useState(isoDay(new Date(Date.now() - 30 * 86400000)));
  const [customEnd, setCustomEnd] = useState(isoDay(new Date()));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectRows, setProjectRows] = useState([]);
  const [routeRows, setRouteRows] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [source, setSource] = useState("stub");

  const { start, end } = useMemo(
    () => rangeToInstants(rangeId, customStart, customEnd),
    [rangeId, customStart, customEnd]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [byProject, byRoute, byDay] = await Promise.all([
        fetchSpend({ groupBy: "project", start, end }),
        fetchSpend({ groupBy: "route", start, end }),
        fetchSpend({ groupBy: "day", start, end }),
      ]);
      setProjectRows(byProject.rows || []);
      setRouteRows(byRoute.rows || []);
      setDailyRows(byDay.rows || []);
      // All three envelopes share the same source label; the project
      // call is arbitrary as the source-of-truth here.
      setSource(byProject.source || "stub");
    } catch (e) {
      setError(e.message || "Failed to load LLM spend");
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    // A "custom" range with an unresolved (empty / start≥end) window
    // is a user-visible validation state — surface the message and
    // skip the fetch rather than hit the API with rangeStart>rangeEnd.
    if (!start || !end) {
      setLoading(false);
      setError("Custom range: end must be after start");
      setProjectRows([]);
      setRouteRows([]);
      setDailyRows([]);
      return;
    }
    load();
  }, [load, start, end]);

  const mtd = mtdSpend(dailyRows);
  const projected = projectMonth(dailyRows);
  const momPct = momDeltaPct(dailyRows);
  const totalInRange = sumCost(projectRows);

  const projectChartRows = projectRows.slice(0, 8);

  const emptyLine = "No LLM usage yet in this range";

  return (
    <Box
      component={motion.div}
      variants={motionVariants.heroEnter}
      initial="initial"
      animate="animate"
      sx={{
        minHeight: "calc(100vh - 64px)",
        p: { xs: 2, md: 4 },
        ...pageBackground(isDark),
      }}
      data-testid="llm-spend-page"
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ ...heroText(isDark) }}>
          LLM Spend
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Usage + cost across every model and route your workloads hit,
          aggregated from the platform LLM gateway.
        </Typography>
      </Box>

      {/* Range picker */}
      <Paper sx={{ p: 2, mb: 3, ...glassCard(isDark) }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <ToggleButtonGroup
            value={rangeId}
            exclusive
            onChange={(_, v) => v && setRangeId(v)}
            size="small"
            data-testid="range-picker"
          >
            {RANGE_OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.id}
                value={opt.id}
                data-testid={`range-${opt.id}`}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {rangeId === "custom" && (
            <>
              <TextField
                type="date"
                label="Start"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                data-testid="range-custom-start"
              />
              <TextField
                type="date"
                label="End"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                data-testid="range-custom-end"
              />
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            size="small"
            label={`source: ${source}`}
            data-testid="source-chip"
            color={source === "stub" ? "default" : "primary"}
          />
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress data-testid="llm-spend-loading" />
        </Box>
      ) : error ? (
        <Alert severity="error" data-testid="llm-spend-error">
          {error}
        </Alert>
      ) : (
        <>
          {/* KPIs */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 3 }}
            data-testid="kpi-row"
          >
            <KpiCard
              label="Current-month spend"
              value={formatDollars(mtd)}
              sub="Month-to-date (UTC)"
              testId="kpi-mtd"
            />
            <KpiCard
              label="Projected month-end"
              value={formatDollars(projected)}
              sub="Straight-line from MTD"
              testId="kpi-projected"
            />
            <KpiCard
              label="MoM delta"
              value={formatDelta(momPct)}
              sub={momPct == null ? "No prior-month spend to compare" : "vs previous whole month"}
              testId="kpi-mom"
            />
            <KpiCard
              label="Total in range"
              value={formatDollars(totalInRange)}
              sub={`${projectRows.length} project${projectRows.length === 1 ? "" : "s"} with spend`}
              testId="kpi-total"
            />
          </Stack>

          {source === "stub" && (
            <Alert severity="info" sx={{ mb: 3 }} data-testid="stub-source-banner">
              LLM usage capture is still being wired up — this page will
              start showing numbers as soon as the platform gateway
              begins emitting per-request metrics.
            </Alert>
          )}

          {/* Per-project breakdown table + chart */}
          {projectRows.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, ...glassCard(isDark) }}>
              <Typography variant="h6">Spend by project</Typography>
              <BarChart
                dataset={projectChartRows.map((r) => ({
                  project: r.label || r.groupKey,
                  cost: Number(r.costUsd) || 0,
                }))}
                xAxis={[{ scaleType: "band", dataKey: "project" }]}
                series={[{ dataKey: "cost", label: "USD" }]}
                height={280}
                data-testid="project-chart"
              />
            </Paper>
          )}
          <BreakdownTable
            title="Per-project breakdown"
            rows={projectRows}
            keyLabel="Project"
            testId="project-table"
            emptyText={emptyLine}
          />

          <Divider sx={{ my: 3 }} />

          <BreakdownTable
            title="Per-route breakdown"
            rows={routeRows}
            keyLabel="Route (provider:model)"
            testId="route-table"
            emptyText={emptyLine}
          />
        </>
      )}
    </Box>
  );
};

export default LlmSpendPage;
